import React, { useEffect, useMemo, useState } from "react"
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Alert } from "reactstrap"
import { useForm, Controller } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { useQuery, useMutation } from "@apollo/client"
import gql from "graphql-tag"
import { TPAFormSchema } from "./TPAModalsSchema"
import Select from "react-select"
import Loader from "../../Material/Loader"
import "./Modals.css"


const GetTPAQuery = gql`
  query GetTPA($id: ID!) {
    tpa(id: $id) {
      id
      pk
      dateAdded
      dateModified
      name
      feeType
      feeAmount
      contactName
      contactEmail
      description
    }
  }
`

const TPAFeeTypeChoicesQuery = gql`
  query TPAFeeTypeChoices {
    tpaFeeTypeChoices: __type(name: "IncidentsTPAFeeTypeChoices") {
      enumValues {
        name
        description
      }
    }
  }
`

const CreateTPAMutation = gql`
  mutation CreateTPA($input: CreateTPAMutationInput!) {
    createTpa(input: $input) {
      ok
      errors {
        field
        messages
      }
    }
  }
`

const UpdateTPAMutation = gql`
  mutation UpdateTPA($input: UpdateTPAMutationInput!) {
    updateTpa(input: $input) {
      ok
      errors {
        field
        messages
      }
    }
  }
`

const CreateUpdateTPAModal = ({ isOpen, toggle, id, refetchQuery }) => {
    const [mutationError, setMutationError] = useState(null)
    const isUpdate = !!id
    const { data: tpaData, loading: tpaLoading } = useQuery(GetTPAQuery, {
        variables: { id },
        skip: !isUpdate,
        fetchPolicy: "network-only"
    })

    const { data: feeTypeData, loading: feeTypeLoading } = useQuery(TPAFeeTypeChoicesQuery, {
        fetchPolicy: "network-only"
    })

    const [createTpa, { loading: creating }] = useMutation(CreateTPAMutation)
    const [updateTpa, { loading: updating }] = useMutation(UpdateTPAMutation)

    const { control, handleSubmit, reset, formState: { errors } } = useForm({
        resolver: yupResolver(TPAFormSchema)
    })

    const feeTypeOptions = useMemo(() => (
        feeTypeData?.tpaFeeTypeChoices?.enumValues?.map(opt => ({
            value: opt.name,
            label: opt.description || opt.name
        })) || []
    ), [feeTypeData])

    useEffect(() => {
        if (isUpdate && tpaData?.tpa && feeTypeOptions.length > 0) {
            reset({
                name: tpaData.tpa.name || "",
                feeType: tpaData.tpa.feeType ?? "",
                feeAmount: tpaData.tpa.feeAmount ?? "",
                contactName: tpaData.tpa.contactName ?? "",
                contactEmail: tpaData.tpa.contactEmail ?? "",
                description: tpaData.tpa.description ?? "",
            })
        } else {
            reset({
                name: "",
                feeType: "",
                feeAmount: "",
                contactName: "",
                contactEmail: "",
                description: "",
            })
        }
    }, [tpaData, isUpdate, reset, feeTypeOptions])


    const onSubmit = async (formData) => {
        try {
            setMutationError(null)
            let result
            if (isUpdate) {
                result = await updateTpa({ variables: { input: { ...formData, tpaId: id } } })
            } else {
                result = await createTpa({ variables: { input: formData } })
            }
            const mutationResult = result?.data?.createTpa || result?.data?.updateTpa
            if (mutationResult && mutationResult.ok) {
                toggle()
                if (refetchQuery) await refetchQuery()
            } else if (mutationResult && mutationResult.errors && mutationResult.errors.length > 0) {
                setMutationError(mutationResult.errors.map(e => e.messages).flat().join(", "))
            } else {
                setMutationError("Unknown error occurred.")
            }
        } catch (error) {
            setMutationError(error.message || "Unknown error occurred.")
        }
    }

    const loading = isUpdate ? tpaLoading || feeTypeLoading : feeTypeLoading

    return (
        <Modal className="tpa-modal modal-dialog-centered" isOpen={isOpen} size="lg">
            <ModalHeader>{isUpdate ? "Update TPA" : "Create TPA"}</ModalHeader>
            <Form onSubmit={handleSubmit(onSubmit)}>
                <ModalBody>
                    {loading ? <Loader /> : (
                        <>
                            <FormGroup>
                                <Label for="name">Name</Label>
                                <Controller
                                    name="name"
                                    control={control}
                                    defaultValue=""
                                    render={({ field }) => (
                                        <Input
                                            id="name"
                                            value={field.value ?? ""}
                                            onChange={e => field.onChange(e.target.value)}
                                            invalid={!!errors.name}
                                        />
                                    )}
                                />
                                {errors.name && <div className="text-danger">{errors.name.message}</div>}
                            </FormGroup>
                            <FormGroup>
                                <Label for="feeType">Fee Type</Label>
                                <Controller
                                    name="feeType"
                                    control={control}
                                    defaultValue=""
                                    render={({ field }) => (
                                        <Select
                                            options={feeTypeOptions}
                                            placeholder="Select Fee Type"
                                            className={errors.feeType ? "is-invalid" : ""}
                                            value={feeTypeOptions.find(opt => opt.value === field.value) || null}
                                            onChange={opt => field.onChange(opt ? opt.value : '')}
                                        />
                                    )}
                                />
                                {errors.feeType && <div className="text-danger">{errors.feeType.message}</div>}
                            </FormGroup>
                            <FormGroup>
                                <Label for="feeAmount">Fee Amount</Label>
                                <Controller
                                    name="feeAmount"
                                    control={control}
                                    defaultValue=""
                                    render={({ field }) => (
                                        <Input
                                            id="feeAmount"
                                            type="number"
                                            value={field.value ?? ""}
                                            onChange={e => field.onChange(e.target.value)}
                                            invalid={!!errors.feeAmount}
                                        />
                                    )}
                                />
                                {errors.feeAmount && <div className="text-danger">{errors.feeAmount.message}</div>}
                            </FormGroup>
                            <FormGroup>
                                <Label for="contactName">Contact Name</Label>
                                <Controller
                                    name="contactName"
                                    control={control}
                                    defaultValue=""
                                    render={({ field }) => (
                                        <Input
                                            id="contactName"
                                            value={field.value ?? ""}
                                            onChange={e => field.onChange(e.target.value)}
                                            invalid={!!errors.contactName}
                                        />
                                    )}
                                />
                                {errors.contactName && <div className="text-danger">{errors.contactName.message}</div>}
                            </FormGroup>
                            <FormGroup>
                                <Label for="contactEmail">Contact Email</Label>
                                <Controller
                                    name="contactEmail"
                                    control={control}
                                    defaultValue=""
                                    render={({ field }) => (
                                        <Input
                                            id="contactEmail"
                                            type="email"
                                            value={field.value ?? ""}
                                            onChange={e => field.onChange(e.target.value)}
                                            invalid={!!errors.contactEmail}
                                        />
                                    )}
                                />
                                {errors.contactEmail && <div className="text-danger">{errors.contactEmail.message}</div>}
                            </FormGroup>
                            <FormGroup>
                                <Label for="description">Description</Label>
                                <Controller
                                    name="description"
                                    control={control}
                                    defaultValue=""
                                    render={({ field }) => (
                                        <Input
                                            id="description"
                                            type="textarea"
                                            value={field.value ?? ""}
                                            onChange={e => field.onChange(e.target.value)}
                                            invalid={!!errors.description}
                                        />
                                    )}
                                />
                                {errors.description && <div className="text-danger">{errors.description.message}</div>}
                            </FormGroup>
                        </>
                    )}
                    {mutationError && <Alert color="danger" className="mb-2">{mutationError}</Alert>}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggle}>Cancel</Button>
                    <Button color="primary" type="submit" disabled={creating || updating}>
                        {isUpdate ? "Update" : "Create"}
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    )
}
export default CreateUpdateTPAModal
